import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { usePreloadedInfiniteQuery } from "../../hooks/usePreloadedInfiniteQuery";
import { takeProducts } from "./takeProducts";
import { fromZonedTime } from "date-fns-tz";
import dayjs from "dayjs";

const getNextPageParam = (lastPage: any, pages: any) =>
  lastPage.last ? undefined : pages.length;

const opts = { getNextPageParam };

export const useFetchCities = ({ pageSize = 12 }: { pageSize?: number }) => {
  return useInfiniteQuery<PageResult<CityDTO>>(
    ["cities"],
    async ({ pageParam = 0 }) => {
      const result = await takeProducts({
        pageNum: pageParam,
        pageSize,
        showHome: undefined,
        productType: "CITY",
      });
      return result?.cities || [];
    },
    opts
  );
};

export const useFetchCitiesPreload = ({
  pageSize = 12,
}: {
  pageSize?: number;
}) => {
  return usePreloadedInfiniteQuery<PageResult<CityDTO>>(
    ["cities"],
    async ({ pageParam = 0 }) => {
      const result = await takeProducts({
        pageNum: pageParam,
        pageSize,
        showHome: undefined,
        productType: "CITY",
      });
      return result?.cities || [];
    },
    opts
  );
};
export const useHomeProductsQuery = ({
  productType,
  pageSize,
}: {
  productType: TProductType | "EVENT" | "CITY";
  pageSize?: number;
}) => {
  return useInfiniteQuery<HomePageResponse>(
    ["home", productType],
    async ({ pageParam = 0 }) => {
      const result = await takeProducts({
        pageNum: pageParam,
        pageSize: pageSize ?? 5,
        showHome: true,
        productType,
      });
      return result;
    },
    opts
  );
};

export const useHomeProducts = (
  productType: TProductType,
  key: keyof HomePageResponse
) => {
  const { data, fetchNextPage, hasNextPage } = useHomeProductsQuery({
    productType,
    pageSize: productType === "MUSICAL" ? 999 : 5,
  });
  const products: ProductBaseResponse[] = useMemo(
    () =>
      data?.pages?.flatMap((x: HomePageResponse) => {
        return (x[key]?.data ?? []) as ProductBaseResponse[];
      }) ?? [],
    [data, key]
  );

  // Handle swiper slide change
  const handleSlideChange = (swiper: any) => {
    const currentIndex = swiper.activeIndex;
    const totalSlides = products.length;
    // Trigger API call when user swipes near the end
    if (currentIndex >= totalSlides - 4 && hasNextPage) {
      fetchNextPage();
    }
  };

  return {
    products,
    handleSlideChange,
  };
};
export const useHomeEvents = () => {
  const { data, fetchNextPage, hasNextPage } = useHomeProductsQuery({
    productType: "EVENT",
    pageSize: 5,
  });

  const formatDate = (date: Date | null | undefined): Date | null => {
    if (!date) return null;
    // Convert to New York timezone and set to start of day
    const timeZone = "America/New_York";
    const zonedDate = fromZonedTime(date, timeZone);
    return new Date(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate());
  }

  const events: EventDTO[] = useMemo(
    () =>
      data?.pages?.flatMap((x: HomePageResponse) => {
        let events: EventDTO[] = [];
        if (x["events"]?.data) {
          for (const event of x["events"].data) {
            const startDate = event.startDate ? formatDate(new Date(event.startDate)) : null;
            const endDate = event.endDate ? formatDate(new Date(event.endDate)) : null;
            events.push({
              ...event,
              startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
              endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
            });
          }
        }
        return events;
      }) ?? [],
    [data]
  );

  // Handle swiper slide change
  const handleSlideChange = (swiper: any) => {
    const currentIndex = swiper.activeIndex;
    const totalSlides = events.length;
    // Trigger API call when user swipes near the end
    if (currentIndex >= totalSlides - 4 && hasNextPage) {
      fetchNextPage();
    }
  };

  return {
    events,
    handleSlideChange,
  };
};
